import { useState } from "react";
import { Link } from "react-router-dom";
import { IMAGES, WHATSAPP_URL } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import { Users, Building, Heart, Share2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const pathways = [
  {
    icon: Users,
    title: "Volunteer",
    desc: "Join our on-ground teams across Khunti, Ranchi, Lohardaga, and Hazaribagh. Roles include field coordination, health camp assistance, digital literacy training, and youth mentoring.",
    cta: "Sign Up to Volunteer",
    action: "volunteer",
  },
  {
    icon: Building,
    title: "Partner (CSR & Institutional)",
    desc: "Channel CSR funds and institutional grants into high-impact, fully auditable programmes. Our FPO, hospital, and education initiatives are structured for formal partnerships.",
    cta: "Explore Partnerships",
    action: "partner",
  },
  {
    icon: Heart,
    title: "Contribute",
    desc: "Every contribution is deployed directly into community programmes. Full financial transparency and detailed utilisation reports. Tax benefits under Section 80G.",
    cta: "Get in Touch",
    action: "contribute",
  },
  {
    icon: Share2,
    title: "Spread the Word",
    desc: "Follow us on social media, share our stories, and amplify the voices of the communities we serve.",
    cta: "Connect on Social Media",
    action: "share",
  },
];

export default function GetInvolvedPage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", inquiry_type: "Volunteer", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Thank you! We will get back to you soon.");
        setFormData({ name: "", email: "", phone: "", inquiry_type: "Volunteer", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Unable to submit. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="get-involved-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.events})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/65" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
          <ScrollReveal>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4">
              Jharkhand's Transformation<br />Needs You.
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Four Pathways */}
      <section data-testid="pathways-section" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Ways to Help</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Four Ways to Get Involved</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            {pathways.map((path, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div
                  data-testid={`pathway-card-${path.action}`}
                  className="bg-[#F0F7F4] rounded-xl p-6 md:p-8 border border-[#2D6A4F]/10 hover:shadow-lg transition-all h-full flex flex-col"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#1B4332] flex items-center justify-center mb-5">
                    <path.icon size={24} className="text-[#D4A843]" />
                  </div>
                  <h3 className="font-playfair text-xl text-[#1B4332] font-semibold mb-3">{path.title}</h3>
                  <p className="text-[#666] text-sm font-outfit leading-relaxed mb-6 flex-1">{path.desc}</p>
                  {path.action === "share" ? (
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#D4A843] font-semibold font-outfit text-sm hover:gap-3 transition-all"
                    >
                      {path.cta} <ArrowRight size={16} />
                    </a>
                  ) : (
                    <a
                      href="#inquiry-form"
                      className="inline-flex items-center gap-2 text-[#D4A843] font-semibold font-outfit text-sm hover:gap-3 transition-all"
                    >
                      {path.cta} <ArrowRight size={16} />
                    </a>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="inquiry-form" data-testid="inquiry-form" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Reach Out</p>
              <h2 className="font-playfair text-2xl sm:text-3xl text-[#1B4332] font-bold">Get in Touch</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-[0_4px_20px_rgba(27,67,50,0.08)]">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Name *</label>
                  <input
                    type="text"
                    name="name"
                    data-testid="form-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                    placeholder="Your full name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      data-testid="form-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      data-testid="form-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Type of Inquiry</label>
                  <select
                    data-testid="form-inquiry-type"
                    value={formData.inquiry_type}
                    onChange={(e) => setFormData({ ...formData, inquiry_type: e.target.value })}
                    className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit"
                  >
                    <option value="Volunteer">Volunteer</option>
                    <option value="Partner">Partner / CSR</option>
                    <option value="Donate">Donate</option>
                    <option value="Media">Media</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-outfit font-medium text-[#1B4332] mb-1.5">Message *</label>
                  <textarea
                    name="message"
                    data-testid="form-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full bg-white border border-[#2D6A4F]/20 rounded-lg px-4 py-3 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4A843] focus:border-transparent transition-all font-outfit resize-none"
                    placeholder="Tell us how you'd like to help..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={submitting}
                  className="w-full bg-[#D4A843] text-[#1B4332] px-6 py-3.5 rounded-xl font-bold font-outfit text-base hover:bg-[#D4A843]/90 hover:shadow-lg transition-all disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Inquiry"}
                </button>
              </div>
            </form>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
